import type { OrderType } from 'apiConnector/blotters';
import type { AnalyticCategoryActions } from 'models';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { Action } from 'state/actions';
import { getOrderById } from 'state/blotters/secondaryMarketOrderBlotter/secondaryMarketOrderBlotter.selector';
import type { CpOrderCancellationActions } from 'state/secondaryMarket/cpOrder/cancellation/cancellations.actions';
import { cpOrderCancellationActions } from 'state/secondaryMarket/cpOrder/cancellation/cancellations.actions';
import { secondaryMarketCpOrderInitFormFetchRequested } from 'state/secondaryMarket/cpOrder/referential/product/product.actions';
import type { LimitOrderCancellationActions } from 'state/secondaryMarket/limit-order/cancellation/cancellations.actions';
import { limitOrderCancellationActions } from 'state/secondaryMarket/limit-order/cancellation/cancellations.actions';
import { limitOrderActions } from 'state/secondaryMarket/limit-order/limitOrder.actions';
import { liveActions } from 'state/secondaryMarket/live/live.actions';
import { toBcsProductKey } from 'state/secondaryMarket/utils';
import type { Thunk } from 'state/state.common';
import type { SecondaryMarketQuotesTabType } from 'state/workspaces/model';
import {
  openAmendCpOrderTabThunk,
  secondaryMarketPostEventLimitOrderThunks,
  secondaryMarketPostEventLiveThunks,
} from 'state/workspaces/secondary-market/post-event/secondaryMarketPostEvent.thunks';
import { secondaryMarketQuoteWorkspaceAddTab } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.actions';
import type { BcsProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';
import { secondaryDlpQuoteTabCreatedThunk } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.thunks';
import { workspaceTabCreated } from 'state/workspaces/workspace.actions';

import { secondaryMarketCpOrderByOrderIdReloadRequested } from './cpOrder';
import type { LiveCancellationActions } from './live/cancellation/cancellations.actions';
import { liveTradeCancellationActions } from './live/cancellation/cancellations.actions';
import { secondaryMarketOpenTabFromTradeAllocationRequested } from './openTabFromTradeAllocation.actions';

const cancellationOptionsByOrderTypeMap: Record<
  OrderType,
  {
    actions: LimitOrderCancellationActions | LiveCancellationActions | CpOrderCancellationActions;
    logAnalyticValue: string;
  }
> = {
  'Limit order': { actions: limitOrderCancellationActions, logAnalyticValue: 'Limit order' },
  Live: { actions: liveTradeCancellationActions, logAnalyticValue: 'Live order' },
  'Order on close': { actions: cpOrderCancellationActions, logAnalyticValue: 'Order' },
};
export function secondaryMarketOrderCancellationWorkflowAbortedThunk(orderId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const item = getOrderById(state, orderId);
    if (!item?.orderType) {
      return;
    }
    const cancellationOptions = cancellationOptionsByOrderTypeMap[item.orderType];
    logAnalytics.trackEvent(
      'Secondary market',
      `${cancellationOptions.logAnalyticValue} cancellation workflow aborted` as AnalyticCategoryActions['Secondary market'],
    );
    dispatch(cancellationOptions.actions.secondaryMarketCancellationWorkflowAborted(orderId));
  };
}

export function secondaryMarketOrderCancellationWorkflowCompletedThunk(orderId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const item = getOrderById(state, orderId);
    if (!item?.orderType) {
      return;
    }
    const cancellationOptions = cancellationOptionsByOrderTypeMap[item.orderType];
    logAnalytics.trackEvent(
      'Secondary market',
      `${cancellationOptions.logAnalyticValue} cancellation workflow completed` as AnalyticCategoryActions['Secondary market'],
    );
    dispatch(cancellationOptions.actions.secondaryMarketCancellationWorkflowCompleted(orderId));
  };
}

export function secondaryMarketOrderCancellationRequestedThunk(orderId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const item = getOrderById(state, orderId);

    if (!item?.orderType) {
      return;
    }
    const cancellationOptions = cancellationOptionsByOrderTypeMap[item.orderType];
    logAnalytics.trackEvent(
      'Secondary market',
      `${cancellationOptions.logAnalyticValue} cancellation workflow requested` as AnalyticCategoryActions['Secondary market'],
    );
    dispatch(cancellationOptions.actions.secondaryMarketCancellationRequested(orderId));
  };
}

export function secondaryMarketOrderCancellationWorkflowInitThunk(orderId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const item = getOrderById(state, orderId);
    if (!item?.orderType) {
      return;
    }
    const cancellationOptions = cancellationOptionsByOrderTypeMap[item.orderType];
    logAnalytics.trackEvent(
      'Secondary market',
      `${cancellationOptions.logAnalyticValue} cancellation workflow initiated` as AnalyticCategoryActions['Secondary market'],
    );
    dispatch(cancellationOptions.actions.secondaryMarketCancellationWorkflowInit(orderId));
  };
}

export function secondaryMarketOrderPostTradeEditThunk(orderId: string, tabName: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const item = getOrderById(state, orderId);
    switch (item?.orderType) {
      case 'Order on close':
        logAnalytics.trackEvent('Secondary market', 'Amend CP order form');
        dispatch(openAmendCpOrderTabThunk(orderId, tabName));
        break;
      case 'Live':
        logAnalytics.trackEvent('Secondary market', 'Amend live trade form');
        dispatch(secondaryMarketPostEventLiveThunks.openAmendTabThunk(orderId, tabName));
        break;
      case 'Limit order':
        logAnalytics.trackEvent('Secondary market', 'Amend limit order form');
        dispatch(secondaryMarketPostEventLimitOrderThunks.openAmendTabThunk(orderId, tabName));
        break;
    }
  };
}

export function secondaryMarketOrderOpenNewOrderTabThunk(orderId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const item = getOrderById(state, orderId);
    switch (item?.orderType) {
      case 'Order on close':
        logAnalytics.trackEvent('Secondary market', 'Open CP Order form');
        dispatch(secondaryMarketCpOrderByOrderIdReloadRequested(orderId));
        break;
      case 'Live':
        logAnalytics.trackEvent('Secondary market', 'Open RT quote form');
        dispatch(liveActions.secondaryMarketReloadRequestedByOrderId(orderId));
        break;
      case 'Limit order':
        logAnalytics.trackEvent('Secondary market', 'Open Limit order form');
        dispatch(limitOrderActions.secondaryMarketReloadRequestedByOrderId(orderId));
        break;
    }
  };
}

export type SecondaryMarketTabTarget =
  | { formType: 'Ebiz screen'; isin: string | undefined }
  | { formType: OrderType; bcsKey: string; isin: string };

type OrderTabConfig = {
  tabType: SecondaryMarketQuotesTabType;
  tabNamePrefix: string;
  initFormFetchRequested: (tabId: string, bcsProductKey: BcsProductKey) => Action;
  logAnalyticValue: AnalyticCategoryActions['Secondary market'];
};

const orderTabConfigByOrderType: Record<OrderType, OrderTabConfig> = {
  'Order on close': {
    tabType: 'closePriceOrder',
    tabNamePrefix: 'CP order',
    initFormFetchRequested: secondaryMarketCpOrderInitFormFetchRequested,
    logAnalyticValue: 'Open CP Order form',
  },
  Live: {
    tabType: 'live',
    tabNamePrefix: 'Live',
    initFormFetchRequested: liveActions.initFormFetchRequested,
    logAnalyticValue: 'Open RT quote form',
  },
  'Limit order': {
    tabType: 'limitOrder',
    tabNamePrefix: 'Limit order',
    initFormFetchRequested: limitOrderActions.initFormFetchRequested,
    logAnalyticValue: 'Open Limit order form',
  },
};

export function secondaryMarketOrderOpenNewOrderTabFromIsinOrBcsKeyThunk(
  target: SecondaryMarketTabTarget,
): Thunk<void> {
  return (dispatch, _, { navigateTo }) => {
    if (target.formType === 'Ebiz screen') {
      if (target.isin) {
        dispatch(secondaryDlpQuoteTabCreatedThunk(target.isin));
      }
      navigateTo('/secondary-market-quotes-workspace');
      return;
    }
    logAnalytics.trackEvent('Secondary market', orderTabConfigByOrderType[target.formType].logAnalyticValue);
    dispatch(secondaryMarketOpenTabFromTradeAllocationRequested(target.formType, target.bcsKey, target.isin));
  };
}

export function secondaryMarketTabCreatedFromTradeAllocationThunk(
  formType: OrderType,
  bcsKey: string,
  isin: string,
): Thunk<void> {
  return (dispatch, _, { getNewGuid }) => {
    const bcsProductKey = toBcsProductKey(bcsKey);
    if (bcsProductKey === undefined) {
      return;
    }
    const tabId = getNewGuid();
    const { tabType, tabNamePrefix, initFormFetchRequested } = orderTabConfigByOrderType[formType];
    dispatch(secondaryMarketQuoteWorkspaceAddTab(tabId, bcsKey, tabType));
    dispatch(workspaceTabCreated(tabId, 'secondaryMarketQuotes', tabType, `${tabNamePrefix} ${isin}`));
    dispatch(initFormFetchRequested(tabId, bcsProductKey));
  };
}

export function openInModifyCpOrderScreenThunk(orderIds: string[]): Thunk<Promise<void>> {
  return async dispatch => {
    for (const orderId of orderIds) {
      logAnalytics.trackEvent('Secondary market', 'Amend CP order form');
      await dispatch(openAmendCpOrderTabThunk(orderId, `Order ${orderId}`));
    }
  };
}
